import { createPortal } from 'react-dom';
import { WeightGlyph } from '@/components/ui';
import { formatCoordinate, formatResidual } from '@/logic/statistics';
import { getWeightLabel, getWeightStatus, hasResidual } from '@/logic/weights';
import type { PointPair, PointResidual } from '@/types';
import type { MapPoint } from './mapModel';
import { systemName } from './mapModel';
import { unitSuffix } from './mapDisplay';

const CARD_WIDTH = 248;
const CARD_HEIGHT = 168;
const OFFSET = 14;

const Row = ({ label, n, e }: { label: string; n: string; e: string }) => (
  <div className="contents">
    <span className="text-fg-muted">{label}</span>
    <span className="text-right font-mono tabular-nums">{n}</span>
    <span className="text-right font-mono tabular-nums">{e}</span>
  </div>
);

/**
 * Point details beside the pointer (or the focused point). Supplementary and
 * hidden from assistive technology: the point's name, the selected-pair
 * panel, and the table carry the same information accessibly.
 */
export const PointHoverCard = ({
  point,
  x,
  y,
  pair,
  residual,
  flagged,
  canLink,
  units,
}: {
  point: MapPoint;
  x: number;
  y: number;
  pair: PointPair | undefined;
  residual: PointResidual | undefined;
  flagged: boolean;
  canLink: boolean;
  units?: string;
}) => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const left =
    x + OFFSET + CARD_WIDTH > viewportWidth
      ? Math.max(8, x - OFFSET - CARD_WIDTH)
      : x + OFFSET;
  const top =
    y + OFFSET + CARD_HEIGHT > viewportHeight
      ? Math.max(8, y - OFFSET - CARD_HEIGHT)
      : y + OFFSET;
  const suffix = unitSuffix(units);
  const status = pair ? getWeightStatus(pair.weight) : null;

  return createPortal(
    <div
      aria-hidden="true"
      className="pointer-events-none fixed z-50 animate-fade-in rounded-lg border border-border bg-surface-raised px-3 py-2.5 text-xs text-fg shadow-lg"
      style={{ left, top, width: CARD_WIDTH }}
    >
      {pair && status ? (
        <>
          <div className="flex items-center justify-between gap-2">
            <span className="truncate font-mono font-semibold">
              {pair.sys1Point} → {pair.sys2Point}
            </span>
            <span className="flex shrink-0 items-center gap-1 text-fg-muted">
              <WeightGlyph status={status} />
              {getWeightLabel(pair.weight)}
            </span>
          </div>
          {flagged && (
            <p className="mt-0.5 font-medium text-danger-fg">
              Flagged for review
            </p>
          )}
          <div className="mt-2 grid grid-cols-[auto_1fr_1fr] gap-x-3 gap-y-0.5">
            <span />
            <span className="text-right text-[11px] uppercase tracking-wide text-fg-muted">
              North
            </span>
            <span className="text-right text-[11px] uppercase tracking-wide text-fg-muted">
              East
            </span>
            <Row
              label="Record"
              n={formatCoordinate(pair.n1)}
              e={formatCoordinate(pair.e1)}
            />
            <Row
              label="Field"
              n={formatCoordinate(pair.n2)}
              e={formatCoordinate(pair.e2)}
            />
            {residual &&
              residual.total !== null &&
              hasResidual(pair.weight) && (
                <Row
                  label="v"
                  n={formatResidual(residual.vn, 4)}
                  e={formatResidual(residual.ve, 4)}
                />
              )}
          </div>
          {residual && residual.total !== null && hasResidual(pair.weight) && (
            <p className="mt-1.5 flex justify-between border-t border-border pt-1.5">
              <span className="text-fg-muted">Residual</span>
              <span className="font-mono font-medium tabular-nums">
                {formatCoordinate(residual.total)}
                {suffix && ` ${suffix}`}
              </span>
            </p>
          )}
        </>
      ) : (
        <>
          <p className="font-mono font-semibold">
            {systemName(point.system)} {point.label}
          </p>
          <p className="mt-0.5 text-fg-muted">
            Not paired{canLink ? ' · select to link' : ''}
          </p>
        </>
      )}
    </div>,
    document.body
  );
};
